import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SessionWarning from '../components/SessionWarning';
import {
  FileText, Video, BookOpen, Wrench, ExternalLink, Search, LogOut,
  Shield, X, Eye, Download, Lock, Globe, File, Folder, FolderOpen,
  Settings as Settings2, Truck as Truck2, Hammer, Layers, Package, Box,
  Compass, Map as MapIcon, Mountain, Snowflake, Sun, Wind, Camera,
  Image as ImageIcon, Film, Star, Heart, Key, LayoutGrid, List, Table2,
  ChevronRight, Home, ArrowUp, SortAsc, SortDesc
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = { 'Technical Docs': FileText, 'Videos': Video, 'Assembly Guides': BookOpen, 'Tutorials': Wrench };
const FILE_TYPE_COLORS = { video: '#dc2626', pdf: '#dc2626', doc: '#2563eb', link: '#059669', image: '#7c3aed' };

const FOLDER_ICONS_PORTAL = {
  Folder, Wrench, Settings: Settings2, Globe, Shield, Truck: Truck2,
  Hammer, Layers, Package, Box, Compass, Map: MapIcon, Mountain, Snowflake,
  Sun, Wind, Camera, Image: ImageIcon, Film, Star, Heart, Lock, Key,
  BookOpen, FileText, Video,
};

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtFull = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ── Folder Visual (small) ── */
const FolderVisualSmall = ({ folder, size = 16 }) => {
  const token = localStorage.getItem('portal_token');
  const [imgSrc, setImgSrc] = useState('');
  useEffect(() => {
    if (folder.visual_type === 'image' && folder.cover_image) {
      fetch(`${BACKEND_URL}${folder.cover_image}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob()).then(b => setImgSrc(URL.createObjectURL(b)));
    }
  }, [folder.cover_image, folder.visual_type, token]);
  if (folder.visual_type === 'image' && imgSrc)
    return <img src={imgSrc} alt="" style={{ width: size, height: size }} className="rounded object-cover flex-shrink-0" />;
  if (folder.visual_type === 'icon' && folder.icon && FOLDER_ICONS_PORTAL[folder.icon]) {
    const IC = FOLDER_ICONS_PORTAL[folder.icon];
    return <div style={{ width: size, height: size, backgroundColor: folder.color || '#E8520A' }} className="rounded flex items-center justify-center flex-shrink-0"><IC size={size * 0.55} className="text-white" /></div>;
  }
  return <div style={{ width: size, height: size, backgroundColor: folder.color || '#E8520A' }} className="rounded flex-shrink-0" />;
};

/* ── Big Folder Icon (grid view) ── */
const FolderBig = ({ folder }) => {
  const token = localStorage.getItem('portal_token');
  const [imgSrc, setImgSrc] = useState('');
  useEffect(() => {
    if (folder.visual_type === 'image' && folder.cover_image) {
      fetch(`${BACKEND_URL}${folder.cover_image}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob()).then(b => setImgSrc(URL.createObjectURL(b)));
    }
  }, [folder.cover_image, folder.visual_type, token]);

  if (folder.visual_type === 'image' && imgSrc) {
    return <img src={imgSrc} alt={folder.name} className="w-full h-full object-cover rounded-t-xl" />;
  }
  if (folder.visual_type === 'icon' && folder.icon && FOLDER_ICONS_PORTAL[folder.icon]) {
    const IC = FOLDER_ICONS_PORTAL[folder.icon];
    return (
      <div className="w-full h-full flex items-center justify-center rounded-t-xl" style={{ backgroundColor: folder.color || '#E8520A' }}>
        <IC size={40} className="text-white opacity-80" />
      </div>
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center rounded-t-xl" style={{ backgroundColor: folder.color || '#E8520A' }}>
      <Folder size={40} className="text-white opacity-80" />
    </div>
  );
};

/* ── Lightbox / Preview ── */
const PreviewModal = ({ res, onClose }) => {
  const token = localStorage.getItem('portal_token');
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (res.file_id && ['pdf', 'image', 'video'].includes(res.file_type)) {
      setLoading(true);
      fetch(`${BACKEND_URL}${res.url}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob()).then(blob => setBlobUrl(URL.createObjectURL(blob))).finally(() => setLoading(false));
    }
  }, [res.id, res.file_id, res.file_type, res.url, token]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; document.body.style.position = ''; document.body.style.width = ''; };
  }, [onClose]);

  const handleDownload = () => {
    if (res.file_id) {
      fetch(`${BACKEND_URL}${res.url}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = res.original_filename || 'file'; a.click(); });
    } else window.open(res.url, '_blank');
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backgroundColor: "rgba(0,0,0,0.95)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="font-black text-gray-900 text-base truncate">{res.title}</h3>
            <p className="text-xs text-gray-400">{res.original_filename || res.url} · Added {fmt(res.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            {res.url && <button onClick={handleDownload} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white rounded-full" style={{ backgroundColor: '#E8520A' }}><Download size={12} /> {res.file_id ? 'DOWNLOAD' : 'OPEN'}</button>}
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><X size={16} className="text-gray-600" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-50 min-h-0">
          {loading ? <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin" /></div>
            : res.file_type === 'image' && blobUrl ? <div className="flex items-center justify-center p-6"><img src={blobUrl} alt="" className="max-w-full max-h-full object-contain rounded-xl shadow-lg" style={{ maxHeight: '70vh' }} /></div>
            : res.file_type === 'pdf' && blobUrl ? <iframe src={blobUrl} className="w-full border-0" style={{ minHeight: '70vh' }} title={res.title} />
            : res.file_type === 'video' && blobUrl ? <div className="flex items-center justify-center p-6"><video controls className="max-w-full rounded-xl shadow-lg" style={{ maxHeight: '65vh' }}><source src={blobUrl} /></video></div>
            : <div className="text-center py-16 text-gray-400"><File size={40} className="mx-auto mb-3 opacity-30" /><p className="text-sm font-medium text-gray-600 mb-5">{res.original_filename || res.title}</p><button onClick={handleDownload} className="px-5 py-2.5 font-bold text-white rounded-full text-sm" style={{ backgroundColor: '#E8520A' }}>{res.file_id ? 'Download' : 'Open Link'}</button></div>}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════
   MAIN PORTAL PAGE
═══════════════════════ */
const Portal = () => {
  const { user, logout, authHeaders, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null); // null = root
  const [search, setSearch] = useState('');
  const [layout, setLayout] = useState('grid'); // 'grid' | 'list' | 'details'
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'date' | 'type'
  const [sortDir, setSortDir] = useState('asc');
  const [preview, setPreview] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sidebarCategory, setSidebarCategory] = useState('All');
  const [sidebarProduct, setSidebarProduct] = useState('All');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    Promise.all([
      axios.get(`${BACKEND_URL}/api/portal/resources`, { headers: authHeaders() }),
      axios.get(`${BACKEND_URL}/api/portal/folders`, { headers: authHeaders() }),
    ]).then(([rRes, fRes]) => { setResources(rRes.data); setFolders(fRes.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [authLoading, user, navigate, authHeaders]);

  const closePreview = useCallback(() => setPreview(null), []);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  /* Items visible in current "folder" */
  const visibleFolders = currentFolder ? [] : folders;
  const visibleResources = resources.filter(r => {
    const inFolder = currentFolder ? r.folder_id === currentFolder.id : !r.folder_id;
    const q = search.toLowerCase();
    const matchSearch = !q || r.title.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q);
    const matchCat = sidebarCategory === 'All' || r.category === sidebarCategory;
    const matchProd = sidebarProduct === 'All' || r.product === sidebarProduct;
    return inFolder && matchSearch && matchCat && matchProd;
  });

  const sortedResources = [...visibleResources].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'name') cmp = a.title.localeCompare(b.title);
    else if (sortBy === 'date') cmp = new Date(a.created_at) - new Date(b.created_at);
    else if (sortBy === 'type') cmp = a.file_type.localeCompare(b.file_type);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleLogout = () => { logout(); navigate('/'); };
  const openFolder = (f) => { setCurrentFolder(f); setSearch(''); setSelectedItem(null); };
  const goRoot = () => { setCurrentFolder(null); setSelectedItem(null); };

  const SortIcon = ({ field }) => sortBy === field
    ? (sortDir === 'asc' ? <SortAsc size={12} className="inline ml-1" /> : <SortDesc size={12} className="inline ml-1" />)
    : null;

  const handleResourceClick = (res) => {
    setSelectedItem({ type: 'resource', id: res.id });
    if (!res.file_id && res.url) { window.open(res.url, '_blank', 'noopener,noreferrer'); return; }
    if (res.file_id) setPreview(res);
  };

  return (
    <div className="min-h-screen pt-[76px] flex flex-col bg-gray-100" style={{ fontFamily: 'Inter, sans-serif' }}>
      <SessionWarning />
      {/* Header */}
      <div className="bg-zinc-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#E8520A' }}>Partner Portal</p>
            <h1 className="text-2xl font-black text-white uppercase">Resources Library</h1>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && <button onClick={() => navigate('/portal/admin')} className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white border border-orange-500 rounded-full hover:bg-orange-500 transition-all"><Shield size={12} /> Admin</button>}
            <button onClick={handleLogout} className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-gray-400 border border-gray-700 rounded-full hover:text-white transition-all"><LogOut size={12} /> Sign Out</button>
          </div>
        </div>
      </div>

      {/* Explorer Toolbar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 flex-wrap">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <button onClick={goRoot} className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs font-semibold transition-colors ${!currentFolder ? 'text-orange-600 bg-orange-50' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}>
              <Home size={13} /> Library
            </button>
            {currentFolder && (
              <>
                <ChevronRight size={13} className="text-gray-300" />
                <span className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-semibold text-orange-600 bg-orange-50">
                  <FolderVisualSmall folder={currentFolder} size={13} /> {currentFolder.name}
                </span>
              </>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-48 focus:outline-none"
              onFocus={e => (e.target.style.borderColor = '#E8520A')}
              onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
            />
          </div>

          {/* Layout switcher */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            {[{ id: 'grid', Icon: LayoutGrid }, { id: 'list', Icon: List }, { id: 'details', Icon: Table2 }].map(({ id, Icon }) => (
              <button key={id} onClick={() => setLayout(id)}
                className={`p-2 transition-colors ${layout === id ? 'text-white' : 'text-gray-400 hover:text-gray-700 bg-white'}`}
                style={layout === id ? { backgroundColor: '#E8520A' } : {}}
              >
                <Icon size={14} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Explorer body */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 flex gap-6">

        {/* ── Sidebar ── */}
        <aside className="w-52 flex-shrink-0 hidden lg:block">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sticky top-24 space-y-6">

            {/* Folders */}
            {folders.length > 0 && (
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Folders</p>
                <ul className="space-y-0.5">
                  <li>
                    <button onClick={() => { goRoot(); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${!currentFolder ? 'font-bold text-white' : 'text-gray-600 hover:bg-gray-50'}`} style={!currentFolder ? { backgroundColor: '#E8520A' } : {}}>
                      <Home size={13} className="flex-shrink-0" /> All
                    </button>
                  </li>
                  {folders.map(f => (
                    <li key={f.id}>
                      <button onClick={() => openFolder(f)} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 transition-all ${currentFolder?.id === f.id ? 'font-bold text-orange-600 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <FolderVisualSmall folder={f} size={14} />
                          <span className="truncate">{f.name}</span>
                        </span>
                        <span className="text-xs text-gray-300 flex-shrink-0">{resources.filter(r => r.folder_id === f.id).length}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Categories */}
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Categories</p>
              <ul className="space-y-0.5">
                {['All', ...new Set(resources.map(r => r.category))].map(cat => (
                  <li key={cat}>
                    <button
                      onClick={() => setSidebarCategory(cat)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all ${sidebarCategory === cat ? 'font-bold text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                      style={sidebarCategory === cat ? { backgroundColor: '#E8520A' } : {}}
                    >
                      <span>{cat}</span>
                      <span className={`text-xs rounded-full px-1.5 py-0.5 ${sidebarCategory === cat ? 'bg-white bg-opacity-20 text-white' : 'bg-gray-100 text-gray-400'}`}>
                        {cat === 'All' ? resources.length : resources.filter(r => r.category === cat).length}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Products</p>
              <ul className="space-y-0.5">
                {['All', ...new Set(resources.map(r => r.product).filter(Boolean))].map(prod => (
                  <li key={prod}>
                    <button
                      onClick={() => setSidebarProduct(prod)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${sidebarProduct === prod ? 'font-bold text-orange-600 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      {prod}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              {[...Array(12)].map((_, i) => <div key={i} className="bg-white rounded-xl h-32 animate-pulse" />)}
            </div>
          ) : layout === 'grid' ? (
            <div>
              {/* Folders row */}
              {visibleFolders.length > 0 && (
                <div className="mb-6">
                  {!currentFolder && <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Folders</p>}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                    {visibleFolders.map(f => (
                      <div key={f.id}
                        onDoubleClick={() => openFolder(f)}
                        onClick={() => setSelectedItem({ type: 'folder', id: f.id })}
                        className={`rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-150 select-none group ${selectedItem?.id === f.id && selectedItem?.type === 'folder' ? 'border-orange-400 bg-orange-50 shadow-md' : 'border-transparent bg-white hover:border-orange-200 hover:shadow-md'}`}
                      >
                        <div className="h-20 overflow-hidden">
                          <FolderBig folder={f} />
                        </div>
                        <div className="px-2 py-2">
                          <p className="text-xs font-bold text-gray-800 truncate text-center">{f.name}</p>
                          <p className="text-xs text-gray-400 text-center">{resources.filter(r => r.folder_id === f.id).length} items</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resources grid */}
              {sortedResources.length > 0 && (
                <div>
                  {!currentFolder && <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Files</p>}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                    {sortedResources.map(res => {
                      const Icon = CATEGORY_ICONS[res.category] || FileText;
                      const ytId = getYouTubeId(res.url);
                      const isSelected = selectedItem?.id === res.id && selectedItem?.type === 'resource';
                      return (
                        <div key={res.id}
                          onClick={() => setSelectedItem({ type: 'resource', id: res.id })}
                          onDoubleClick={() => handleResourceClick(res)}
                          className={`rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-150 select-none group ${isSelected ? 'border-orange-400 bg-orange-50 shadow-md' : 'border-transparent bg-white hover:border-orange-200 hover:shadow-md'}`}
                        >
                          {ytId ? (
                            <div className="h-20 relative overflow-hidden">
                              <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                <div className="w-7 h-7 bg-red-600 rounded-full flex items-center justify-center">
                                  <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[9px] border-transparent border-l-white ml-0.5" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="h-20 flex items-center justify-center" style={{ backgroundColor: `${FILE_TYPE_COLORS[res.file_type] || '#E8520A'}18` }}>
                              <Icon size={30} style={{ color: FILE_TYPE_COLORS[res.file_type] || '#E8520A', opacity: 0.7 }} />
                            </div>
                          )}
                          <div className="px-2 py-2">
                            <p className="text-xs font-bold text-gray-800 truncate text-center">{res.title}</p>
                            <p className="text-xs text-gray-400 text-center uppercase">{res.file_type}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {visibleFolders.length === 0 && sortedResources.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                  <FolderOpen size={40} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm">{currentFolder ? 'This folder is empty.' : 'No resources yet.'}</p>
                </div>
              )}

              {/* Double-click hint */}
              {selectedItem && (
                <p className="text-xs text-center text-gray-400 mt-6">Double-click to open · Single-click to select</p>
              )}
            </div>

          ) : layout === 'list' ? (
            <div className="space-y-1">
              {visibleFolders.map(f => (
                <div key={f.id}
                  onDoubleClick={() => openFolder(f)}
                  onClick={() => setSelectedItem({ type: 'folder', id: f.id })}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl cursor-pointer transition-colors select-none ${selectedItem?.id === f.id && selectedItem?.type === 'folder' ? 'bg-orange-100' : 'bg-white hover:bg-gray-50'}`}
                >
                  <FolderVisualSmall folder={f} size={22} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{f.name}</p>
                    {f.description && <p className="text-xs text-gray-400 truncate">{f.description}</p>}
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{resources.filter(r => r.folder_id === f.id).length} items</span>
                  <span className="text-xs text-gray-300 flex-shrink-0 w-24 text-right">{fmt(f.created_at)}</span>
                </div>
              ))}
              {sortedResources.map(res => {
                const Icon = CATEGORY_ICONS[res.category] || FileText;
                const isSelected = selectedItem?.id === res.id && selectedItem?.type === 'resource';
                return (
                  <div key={res.id}
                    onClick={() => setSelectedItem({ type: 'resource', id: res.id })}
                    onDoubleClick={() => handleResourceClick(res)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl cursor-pointer transition-colors select-none ${isSelected ? 'bg-orange-100' : 'bg-white hover:bg-gray-50'}`}
                  >
                    <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${FILE_TYPE_COLORS[res.file_type] || '#E8520A'}18` }}>
                      <Icon size={13} style={{ color: FILE_TYPE_COLORS[res.file_type] || '#E8520A' }} />
                    </div>
                    <p className="flex-1 text-sm font-medium text-gray-800 truncate">{res.title}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 flex-shrink-0">{res.file_type.toUpperCase()}</span>
                    <span className="text-xs text-gray-300 flex-shrink-0 w-24 text-right">{fmt(res.created_at)}</span>
                  </div>
                );
              })}
            </div>

          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2.5 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => toggleSort('name')}>
                      <span className="text-xs font-black uppercase tracking-widest text-gray-500">Name <SortIcon field="name" /></span>
                    </th>
                    <th className="text-left px-4 py-2.5 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => toggleSort('type')}>
                      <span className="text-xs font-black uppercase tracking-widest text-gray-500">Type <SortIcon field="type" /></span>
                    </th>
                    <th className="text-left px-4 py-2.5">
                      <span className="text-xs font-black uppercase tracking-widest text-gray-500">Category</span>
                    </th>
                    <th className="text-left px-4 py-2.5">
                      <span className="text-xs font-black uppercase tracking-widest text-gray-500">Access</span>
                    </th>
                    <th className="text-left px-4 py-2.5 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => toggleSort('date')}>
                      <span className="text-xs font-black uppercase tracking-widest text-gray-500">Date Added <SortIcon field="date" /></span>
                    </th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {visibleFolders.map(f => (
                    <tr key={f.id}
                      onDoubleClick={() => openFolder(f)}
                      onClick={() => setSelectedItem({ type: 'folder', id: f.id })}
                      className={`border-b border-gray-50 cursor-pointer transition-colors select-none ${selectedItem?.id === f.id && selectedItem?.type === 'folder' ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-4 py-2.5 flex items-center gap-2.5">
                        <FolderVisualSmall folder={f} size={18} />
                        <span className="font-semibold text-gray-800">{f.name}</span>
                        <span className="text-xs text-gray-400 ml-1">({resources.filter(r => r.folder_id === f.id).length} items)</span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-400">Folder</td>
                      <td className="px-4 py-2.5 text-xs text-gray-400">—</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${f.access_level === 'admin' ? 'bg-gray-700 text-gray-400' : f.access_level === 'specific' ? 'bg-gray-700 text-gray-400' : 'bg-green-100 text-green-600'}`}>
                          {f.access_level === 'admin' ? '🔐' : f.access_level === 'specific' ? '👥' : '🌐'} {f.access_level}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-400">{fmtFull(f.created_at)}</td>
                      <td />
                    </tr>
                  ))}
                  {sortedResources.map(res => {
                    const Icon = CATEGORY_ICONS[res.category] || FileText;
                    const isSelected = selectedItem?.id === res.id && selectedItem?.type === 'resource';
                    return (
                      <tr key={res.id}
                        onClick={() => setSelectedItem({ type: 'resource', id: res.id })}
                        onDoubleClick={() => handleResourceClick(res)}
                        className={`border-b border-gray-50 cursor-pointer transition-colors select-none ${isSelected ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${FILE_TYPE_COLORS[res.file_type] || '#E8520A'}18` }}>
                              <Icon size={13} style={{ color: FILE_TYPE_COLORS[res.file_type] || '#E8520A' }} />
                            </div>
                            <span className="font-medium text-gray-800">{res.title}</span>
                            {res.original_filename && <span className="text-xs text-gray-300 ml-1">{res.original_filename}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">{res.file_type.toUpperCase()}</span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{res.category}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${res.access_level === 'admin' ? 'bg-gray-700 text-gray-400' : res.access_level === 'specific' ? 'bg-gray-700 text-gray-400' : 'bg-green-100 text-green-600'}`}>
                            {res.access_level === 'admin' ? '🔐' : res.access_level === 'specific' ? '👥' : '🌐'} {res.access_level}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-400">{fmtFull(res.created_at)}</td>
                        <td className="px-4 py-2.5">
                          {res.file_id && (
                            <button onClick={(e) => { e.stopPropagation(); setPreview(res); }} className="text-gray-300 hover:text-orange-500 transition-colors">
                              <Eye size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {visibleFolders.length === 0 && sortedResources.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">This folder is empty.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div className="bg-white border-t border-gray-200 px-6 py-2 text-xs text-gray-400 flex items-center justify-between">
        <span>{currentFolder ? `${currentFolder.name} — ` : ''}{sortedResources.length} item{sortedResources.length !== 1 ? 's' : ''}{visibleFolders.length > 0 ? `, ${visibleFolders.length} folder${visibleFolders.length !== 1 ? 's' : ''}` : ''}</span>
        {selectedItem && <span>1 item selected</span>}
      </div>

      {preview && <PreviewModal res={preview} onClose={closePreview} />}
    </div>
  );
};

export default Portal;
